import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Filter, ChevronLeft, ChevronRight, Trash2, Download } from 'lucide-react';
import exportStudiesToCsv from '../../utils/exportCsv';
import { toast, confirm as confirmModal } from '../ui/Toast';

const RHDashboard = ({ onNavigate, onStudySelect }) => {
  const [studies, setStudies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Filtres
  const [filterLevel, setFilterLevel] = useState('');
  const [filterDomain, setFilterDomain] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const headers = { Authorization: `Bearer ${token}` };

      const params = new URLSearchParams();
      if (filterLevel) params.append('level', filterLevel);
      if (filterDomain) params.append('domain', filterDomain);
      if (filterStatus) params.append('status', filterStatus);
      params.append('page', page);
      params.append('limit', limit);

      const [studiesRes, statsRes] = await Promise.all([
        axios.get(`/api/rh/studies?${params.toString()}`, { headers }),
        axios.get('/api/rh/stats', { headers })
      ]);

      setStudies(studiesRes.data.studies || []);
      setTotal(studiesRes.data.total || 0);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Erreur chargement études RH:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, filterLevel, filterDomain, filterStatus]);

  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const deleteStudy = async (e, studyId, title) => {
    e.stopPropagation();
    const confirmed = await confirmModal(`Supprimer "${title}" ?`);
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('oracle_token');
      await axios.delete(`/api/rh/studies/${studyId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-700' },
      measuring: { label: 'Mesure', cls: 'bg-blue-100 text-blue-700' },
      calculating: { label: 'Calcul', cls: 'bg-yellow-100 text-yellow-700' },
      attributing: { label: 'Attribution', cls: 'bg-purple-100 text-purple-700' },
      converting: { label: 'Conversion', cls: 'bg-indigo-100 text-indigo-700' },
      completed: { label: 'Terminée', cls: 'bg-green-100 text-green-700' },
      archived: { label: 'Archivée', cls: 'bg-gray-200 text-gray-500' }
    };
    const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
    return <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
  };

  const getLevelBadge = (level) => {
    const colors = {
      R1: 'bg-blue-100 text-blue-700',
      R2: 'bg-green-100 text-green-700',
      R3: 'bg-yellow-100 text-yellow-700',
      R4: 'bg-orange-100 text-orange-700',
      R5: 'bg-red-100 text-red-700'
    };
    return <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${colors[level] || 'bg-gray-100'}`}>{level}</span>;
  };

  const getDomainLabel = (domain) => {
    const map = { humain: 'Humain', ecologique: 'Écologique', culturel: 'Culturel' };
    return map[domain] || domain;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">⚖️ Rendement Humain</h1>
          <p className="text-gray-500 text-sm">{total} étude{total > 1 ? 's' : ''} • Protocole 7bis</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => exportStudiesToCsv(studies)}
            disabled={studies.length === 0 || loading}
            className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-lg transition-colors text-sm disabled:opacity-50"
          >
            <Download size={16} />
            CSV
          </button>
          <button
            onClick={() => onNavigate('rh-new')}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-lg transition-colors shadow-sm"
          >
            <Plus size={18} />
            Nouvelle étude
          </button>
        </div>
      </div>

      {/* Compteurs stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total_studies || 0}</div>
            <div className="text-sm text-gray-500">Total études</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-green-600">{stats.by_status?.completed || 0}</div>
            <div className="text-sm text-gray-500">Terminées</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.by_status?.draft || 0}</div>
            <div className="text-sm text-gray-500">Brouillons</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">
              {stats.avg_rh != null ? Number(stats.avg_rh).toFixed(2) : '—'}
            </div>
            <div className="text-sm text-gray-500">RH moyen</div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="flex items-center gap-4 mb-4">
        <Filter size={16} className="text-gray-400" />
        <select
          value={filterLevel}
          onChange={(e) => { setFilterLevel(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Tous niveaux</option>
          <option value="R1">R1</option>
          <option value="R2">R2</option>
          <option value="R3">R3</option>
          <option value="R4">R4</option>
          <option value="R5">R5</option>
        </select>
        <select
          value={filterDomain}
          onChange={(e) => { setFilterDomain(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Tous domaines</option>
          <option value="humain">Humain</option>
          <option value="ecologique">Écologique</option>
          <option value="culturel">Culturel</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Tous statuts</option>
          <option value="draft">Brouillon</option>
          <option value="measuring">Mesure</option>
          <option value="calculating">Calcul</option>
          <option value="attributing">Attribution</option>
          <option value="converting">Conversion</option>
          <option value="completed">Terminée</option>
          <option value="archived">Archivée</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          <span className="ml-3 text-gray-400">Chargement...</span>
        </div>
      ) : studies.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <div className="text-4xl mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucune étude</h3>
          <p className="text-gray-500 mb-4">Créez votre première étude de Rendement Humain</p>
          <button
            onClick={() => onNavigate('rh-new')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Créer une étude
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Titre</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Niveau</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Domaine</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Statut</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Score RH</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Date</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-600 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {studies.map((study) => (
                <tr
                  key={study.id}
                  onClick={() => onStudySelect(study.id)}
                  className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{study.title}</td>
                  <td className="px-4 py-3">{getLevelBadge(study.level)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{getDomainLabel(study.domain)}</td>
                  <td className="px-4 py-3">{getStatusBadge(study.status)}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">
                    {study.rh_score != null ? Number(study.rh_score).toFixed(2) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(study.created_at)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => deleteStudy(e, study.id, study.title)}
                      className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <div className="text-sm text-gray-500">
                Page {page} sur {totalPages} ({total} résultat{total > 1 ? 's' : ''})
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RHDashboard;
